import React, { useState } from 'react';
import { Loader2, Clock, AlertTriangle, PauseCircle, X } from 'lucide-react';
import { useSyncStatus } from '../context/SyncStatusContext';

// Statuses that mean there's something worth showing (not just running/queued)
const VISIBLE_STATUSES = ['queued', 'running', 'error', 'paused_budget'];

const STEP_LABELS = {
  1: 'Importando historial de órdenes (90 días)…',
  2: 'Sincronizando publicaciones inactivas…',
  3: 'Cargando historial de visitas…',
  4: 'Cargando datos de publicidad…',
  5: 'Calculando análisis y estadísticas…',
};

const formatElapsed = (secs) => {
  if (!secs) return null;
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

const formatMinutesAgo = (mins) => {
  if (mins == null) return null;
  if (mins < 60) return `hace ${mins} min`;
  return `hace ${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const SyncStatusBanner = () => {
  const { syncStatus } = useSyncStatus();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !syncStatus) return null;

  const {
    critical_completed,
    critical_sync_status,
    background_status,
    current_step,
    last_completed_step,
    total_running_seconds,
    background_sync_error,
    paused_minutes_ago,
    critical_paused_minutes_ago,
  } = syncStatus;

  // ── Determine what to render ──────────────────────────────────────────────

  // Critical sync paused due to API budget — scheduler will resume it
  const criticalPaused = !critical_completed && critical_sync_status === 'paused_budget';

  // Background sync states
  const bgQueued = background_status === 'queued';
  const bgRunning = background_status === 'running';
  const bgPaused = background_status === 'paused_budget';
  const bgError = background_status === 'error';

  const nothingToShow =
    !criticalPaused &&
    !VISIBLE_STATUSES.includes(background_status);

  if (nothingToShow) return null;

  // ── Render helpers ────────────────────────────────────────────────────────

  const stepLabel = STEP_LABELS[current_step] ?? 'Procesando datos…';
  const lastDoneLabel = last_completed_step > 0
    ? `Paso ${last_completed_step}/5 completado — ${STEP_LABELS[last_completed_step] ?? ''}`
    : 'Ningún paso completado';

  // ── Banner variants ───────────────────────────────────────────────────────

  let icon, colorClasses, title, subtitle;

  if (criticalPaused) {
    icon = <PauseCircle className="w-4 h-4 text-amber-400 shrink-0" />;
    colorClasses = 'bg-amber-500/10 border-amber-500/30';
    title = 'Sincronización inicial pausada — límite de API alcanzado';
    subtitle = `Retomará automáticamente en la próxima ventana horaria${critical_paused_minutes_ago != null ? ` (${formatMinutesAgo(critical_paused_minutes_ago)})` : ''}.`;
  } else if (bgQueued) {
    icon = <Clock className="w-4 h-4 text-amber-400 shrink-0" />;
    colorClasses = 'bg-amber-500/10 border-amber-500/30';
    title = 'Importación completa en cola';
    subtitle = 'Comenzará en breve cuando el sistema esté disponible.';
  } else if (bgRunning) {
    icon = <Loader2 className="w-4 h-4 text-blue-400 animate-spin shrink-0" />;
    colorClasses = 'bg-blue-500/10 border-blue-500/30';
    title = 'Importando historial completo — algunos datos históricos aún no están disponibles';
    subtitle = [
      current_step ? `Paso ${current_step}/5 — ${stepLabel}` : null,
      formatElapsed(total_running_seconds) ? `${formatElapsed(total_running_seconds)} transcurridos` : null,
    ].filter(Boolean).join(' · ');
  } else if (bgPaused) {
    icon = <PauseCircle className="w-4 h-4 text-amber-400 shrink-0" />;
    colorClasses = 'bg-amber-500/10 border-amber-500/30';
    title = 'Importación pausada — límite de API alcanzado';
    subtitle = [
      lastDoneLabel,
      paused_minutes_ago != null ? `Pausado ${formatMinutesAgo(paused_minutes_ago)}` : null,
      'Retomará automáticamente en la próxima ventana horaria.',
    ].filter(Boolean).join(' · ');
  } else if (bgError) {
    icon = <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />;
    colorClasses = 'bg-red-500/10 border-red-500/30';
    title = 'Error en la importación de datos históricos';
    subtitle = [
      lastDoneLabel,
      background_sync_error ? `Error: ${background_sync_error}` : null,
    ].filter(Boolean).join(' · ');
  } else {
    return null;
  }

  return (
    <div className={`border rounded-lg p-3 mb-4 flex items-center justify-between gap-3 ${colorClasses}`}>
      <div className="flex items-center gap-3 min-w-0">
        {icon}
        <div className="min-w-0">
          <span className="text-sm text-slate-300">{title}</span>
          {subtitle && (
            <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
          )}
        </div>
      </div>

      <button
        onClick={() => setDismissed(true)}
        className="text-slate-400 hover:text-white p-1 shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default SyncStatusBanner;
